import React from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { Typography, Input, Collapse, Button, Empty } from 'antd'
import PropTypes from 'prop-types'
import { ImageUploader, RichTextEditor } from '~components'
import { Input as StoryInput } from '~stories'
import { toHyphenatedSlug } from '../../utils/faqSlugFromVenueName'

import DownArrow from '../PositionArrows/DownArrow'
import UpArrow from '../PositionArrows/UpArrow'
import fieldStyles from './FAQAdminFields.module.css'
import styles from './FAQPageSections.module.css'

const { Text } = Typography
const { Panel } = Collapse

const stripHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return ''
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

const PanelActions = ({ onMoveUp, onMoveDown, onDelete, showUp, showDown, deleteLabel }) => (
  <div className={fieldStyles.panelActions} onClick={e => e.stopPropagation()}>
    {showUp && (
      <span className={styles.arrowButton} onClick={onMoveUp} role="button" tabIndex={0}>
        <UpArrow />
      </span>
    )}
    {showDown && (
      <span className={styles.arrowButton} onClick={onMoveDown} role="button" tabIndex={0}>
        <DownArrow />
      </span>
    )}
    <Button danger onClick={onDelete} size="small" type="primary">
      {deleteLabel}
    </Button>
  </div>
)

PanelActions.propTypes = {
  deleteLabel: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func,
  onMoveUp: PropTypes.func,
  showDown: PropTypes.bool,
  showUp: PropTypes.bool
}

const FAQPageSections = ({ sections, setSections }) => {
  const sectionList = sections || []
  const totalQuestions = sectionList.reduce((sum, s) => sum + (s?.questions?.length || 0), 0)

  const updateSections = (updater) => {
    setSections(prevSections => updater(prevSections || []))
  }

  const addSection = () => {
    updateSections(prev => [...prev, { title: '', anchor: '', questions: [] }])
  }

  const deleteSection = (event, index) => {
    event.stopPropagation()
    updateSections(prev => {
      const updated = [...prev]
      updated.splice(index, 1)
      return updated
    })
  }

  const handleSectionTitleChange = (e, index) => {
    const title = e.target.value
    updateSections(prev => {
      const updated = [...prev]
      const current = updated[index] || {}
      const previousAnchor = toHyphenatedSlug(current.title)
      const next = { ...current, title }
      if (!current.anchor || current.anchor === previousAnchor) {
        next.anchor = toHyphenatedSlug(title)
      }
      updated[index] = next
      return updated
    })
  }

  const handleSectionAnchorChange = (e, index) => {
    updateSections(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], anchor: e.target.value }
      return updated
    })
  }

  const addQuestion = (sectionIndex) => {
    updateSections(prev => {
      const updated = [...prev]
      updated[sectionIndex] = {
        ...updated[sectionIndex],
        questions: [...(updated[sectionIndex].questions || []), { question: '', answer: '', image: { url: '', alt: '', caption: '' } }]
      }
      return updated
    })
  }

  const deleteQuestion = (event, sectionIndex, qIndex) => {
    event.stopPropagation()
    updateSections(prev => {
      const updated = [...prev]
      const questions = [...updated[sectionIndex].questions]
      questions.splice(qIndex, 1)
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      return updated
    })
  }

  const handleQuestionChange = (question, sectionIndex, qIndex) => {
    updateSections(prev => {
      const updated = [...prev]
      const questions = [...updated[sectionIndex].questions]
      questions[qIndex] = { ...questions[qIndex], question }
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      return updated
    })
  }

  const handleAnswerChange = (answer, sectionIndex, qIndex) => {
    updateSections(prev => {
      const updated = [...prev]
      const questions = [...updated[sectionIndex].questions]
      questions[qIndex] = { ...questions[qIndex], answer }
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      return updated
    })
  }

  const handleQuestionImageUpload = (url, sectionIndex, qIndex) => {
    updateSections(prev => {
      const updated = [...prev]
      const questions = [...updated[sectionIndex].questions]
      questions[qIndex] = {
        ...questions[qIndex],
        image: { ...(questions[qIndex].image || {}), url, src: url, alt: url.split('/').pop() }
      }
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      return updated
    })
  }

  const handleQuestionImageCaptionChange = (e, sectionIndex, qIndex) => {
    updateSections(prev => {
      const updated = [...prev]
      const questions = [...updated[sectionIndex].questions]
      questions[qIndex] = {
        ...questions[qIndex],
        image: { ...(questions[qIndex].image || {}), caption: e.target.value }
      }
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      return updated
    })
  }

  const moveSection = (index, direction) => {
    updateSections(prev => {
      const target = index + direction
      if (target < 0 || target >= prev.length) return prev
      const updated = [...prev]
      const temp = updated[index]
      updated[index] = updated[target]
      updated[target] = temp
      return updated
    })
  }

  const moveQuestion = (sectionIndex, qIndex, direction) => {
    updateSections(prev => {
      const questions = prev[sectionIndex]?.questions || []
      const target = qIndex + direction
      if (target < 0 || target >= questions.length) return prev
      const updated = [...prev]
      const updatedQuestions = [...updated[sectionIndex].questions]
      const temp = updatedQuestions[qIndex]
      updatedQuestions[qIndex] = updatedQuestions[target]
      updatedQuestions[target] = temp
      updated[sectionIndex] = { ...updated[sectionIndex], questions: updatedQuestions }
      return updated
    })
  }

  return (
    <div className={styles.container}>
      <div className={fieldStyles.toolbar}>
        <Text className={fieldStyles.toolbarMeta}>
          {sectionList.length} section{sectionList.length === 1 ? '' : 's'} · {totalQuestions} question{totalQuestions === 1 ? '' : 's'}
        </Text>
        <Button icon={<PlusOutlined />} onClick={addSection} type="primary">
          Add Section
        </Button>
      </div>

      {sectionList.length === 0 ? (
        <div className={fieldStyles.emptyState}>
          <Empty
            description="No FAQ sections yet. Add a section for each category (e.g. Ticketing, Group Tickets)."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button icon={<PlusOutlined />} onClick={addSection} type="primary">
              Add first section
            </Button>
          </Empty>
        </div>
      ) : (
        <Collapse
          accordion
          className={styles.sectionCollapse}
          defaultActiveKey={sectionList.length ? ['0'] : undefined}
          destroyInactivePanel={false}
          expandIconPosition="right"
        >
          {sectionList.map((section, sectionIndex) => {
            const questionCount = section?.questions?.length || 0
            const sectionTitle = section?.title || `Section ${sectionIndex + 1}`

            return (
              <Panel
                key={String(sectionIndex)}
                extra={
                  <PanelActions
                    deleteLabel="Delete"
                    onDelete={e => deleteSection(e, sectionIndex)}
                    onMoveDown={e => { e.stopPropagation(); moveSection(sectionIndex, 1) }}
                    onMoveUp={e => { e.stopPropagation(); moveSection(sectionIndex, -1) }}
                    showDown={sectionIndex < sectionList.length - 1}
                    showUp={sectionIndex > 0}
                  />
                }
                header={
                  <span>
                    <span className={fieldStyles.panelTitle}>{sectionTitle}</span>
                    <span className={fieldStyles.panelMeta}>
                      {questionCount} question{questionCount === 1 ? '' : 's'}
                      {section?.anchor ? ` · #${section.anchor}` : ''}
                    </span>
                  </span>
                }
              >
                <div className={fieldStyles.fieldGroup}>
                  <div className={fieldStyles.fieldRow}>
                    <Text className={fieldStyles.fieldLabel}>Section Title*</Text>
                    <StoryInput
                      onChange={e => handleSectionTitleChange(e, sectionIndex)}
                      placeholder="Ticketing Information"
                      value={section?.title || ''}
                    />
                  </div>
                  <div className={fieldStyles.fieldRow}>
                    <Text className={fieldStyles.fieldLabel}>Anchor (navigation)</Text>
                    <StoryInput
                      onChange={e => handleSectionAnchorChange(e, sectionIndex)}
                      placeholder="ticketing"
                      value={section?.anchor || ''}
                    />
                    {section?.anchor && (
                      <Text className={fieldStyles.hint}>
                        Jump link: <span className={fieldStyles.anchorPreview}>#{section.anchor}</span>
                      </Text>
                    )}
                  </div>
                </div>

                <div className={fieldStyles.nestedCard}>
                  <div className={fieldStyles.nestedCardHeader}>
                    <Text className={fieldStyles.nestedCardTitle}>Questions &amp; Answers</Text>
                    <Button icon={<PlusOutlined />} onClick={() => addQuestion(sectionIndex)} size="small" type="primary">
                      Add Question
                    </Button>
                  </div>
                  <div className={fieldStyles.nestedCardBody}>
                    {questionCount === 0 ? (
                      <div className={fieldStyles.emptyState}>
                        <Empty
                          description="No questions in this section yet."
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                          <Button onClick={() => addQuestion(sectionIndex)} size="small" type="primary">
                            Add question
                          </Button>
                        </Empty>
                      </div>
                    ) : (
                      <Collapse
                        accordion
                        destroyInactivePanel={false}
                        expandIconPosition="right"
                      >
                        {(section.questions || []).map((item, qIndex) => (
                          <Panel
                            key={String(qIndex)}
                            extra={
                              <PanelActions
                                deleteLabel="Delete"
                                onDelete={e => deleteQuestion(e, sectionIndex, qIndex)}
                                onMoveDown={e => { e.stopPropagation(); moveQuestion(sectionIndex, qIndex, 1) }}
                                onMoveUp={e => { e.stopPropagation(); moveQuestion(sectionIndex, qIndex, -1) }}
                                showDown={qIndex < questionCount - 1}
                                showUp={qIndex > 0}
                              />
                            }
                            header={stripHtml(item?.question) || `Question ${qIndex + 1}`}
                          >
                            <div className={fieldStyles.fieldRow}>
                              <Text className={fieldStyles.fieldLabel}>Question*</Text>
                              <RichTextEditor
                                minHeight={120}
                                onChange={question => handleQuestionChange(question, sectionIndex, qIndex)}
                                placeholder="Write question content…"
                                value={item?.question || ''}
                              />
                            </div>
                            <div className={fieldStyles.fieldRow}>
                              <Text className={fieldStyles.fieldLabel}>Answer</Text>
                              <RichTextEditor
                                minHeight={160}
                                onChange={answer => handleAnswerChange(answer, sectionIndex, qIndex)}
                                placeholder="Write answer content…"
                                value={item?.answer || ''}
                              />
                            </div>
                            <div className={fieldStyles.imageBlock}>
                              <Text className={fieldStyles.fieldLabel}>Image (optional)</Text>
                              <ImageUploader
                                handleUpload={url => handleQuestionImageUpload(url, sectionIndex, qIndex)}
                                logoUrl={item?.image?.src || item?.image?.url}
                                pageType="faq"
                              />
                              {(item?.image?.src || item?.image?.url) && (
                                <div className={fieldStyles.fieldRow} style={{ marginTop: 12, marginBottom: 0 }}>
                                  <Text className={fieldStyles.fieldLabel}>Image Caption</Text>
                                  <Input
                                    onChange={e => handleQuestionImageCaptionChange(e, sectionIndex, qIndex)}
                                    placeholder="Caption for accessibility"
                                    value={item?.image?.caption || ''}
                                  />
                                </div>
                              )}
                            </div>
                          </Panel>
                        ))}
                      </Collapse>
                    )}
                  </div>
                </div>
              </Panel>
            )
          })}
        </Collapse>
      )}
    </div>
  )
}

FAQPageSections.propTypes = {
  sections: PropTypes.array,
  setSections: PropTypes.func.isRequired
}

FAQPageSections.defaultProps = {
  sections: []
}

export default FAQPageSections
